import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Button,
  Box,
  TextField,
  IconButton,
  InputBase,
  Alert,
  CircularProgress,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Save, Preview, Help, Add, EditOutlined, SlideshowOutlined } from '@mui/icons-material';
import { EmptyState, IconTile } from './pagekit';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/EnterpriseAuthContext';
import { supabase } from '../lib/supabase';
import DraggableSlide from './powerPointRichTemplate/DraggableSlide';
import PowerPointUploadSection from './powerPointRichTemplate/PowerPointUploadSection';
import PresentationImportProgress from './presentationImport/PresentationImportProgress';
import PowerPointJsonPanel from './powerPointRichTemplate/PowerPointJsonPanel';
import PowerPointHelpDialog from './powerPointRichTemplate/PowerPointHelpDialog';
import UnsavedChangesDialog from './powerPointRichTemplate/UnsavedChangesDialog';
import useUnsavedChangesGuard from './powerPointRichTemplate/useUnsavedChangesGuard';
import {
  MAX_PROMPT_LENGTH,
  buildPromptCatalog,
  carryPromptsAcrossRename,
  promptsFromCatalog,
  slidesByVariable,
  suggestedPrompts,
  variableNamesInHtml,
} from './powerPointRichTemplate/slidePrompts';
import {
  hasLoadedContent,
  isImportingStatus,
  loadFullTemplate,
} from './powerPointRichTemplate/templateRow';
import { registerUnsavedWorkFlusher, templateEditorPath } from '../utils/navigation';
import DeckTemplateEditor from './deck/DeckTemplateEditor';
import { DECK_TEMPLATE_SCHEMA } from './deck/deckModel';

/** Screen-reader-only text (MUI's visuallyHidden). */
const VISUALLY_HIDDEN = { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/**
 * Skipped slides and warnings an import recorded (generation_metadata), for the notice under the
 * header. Warnings that repeat the AI error (shown in its own notice) are left out.
 * @param {object|null|undefined} metadata - templates.generation_metadata.
 * @param {string|null|undefined} aiError - content.extractionAiError.
 * @returns {{ skipped: Array<{ index: number, reason: string }>, warnings: string[] }}
 */
export function importNotesFrom(metadata, aiError) {
  if (!metadata || typeof metadata !== 'object') return { skipped: [], warnings: [] };
  const skipped = (Array.isArray(metadata.skippedSlides) ? metadata.skippedSlides : [])
    .filter((item) => item && Number.isInteger(item.index) && item.index >= 0)
    .map((item) => ({ index: item.index, reason: String(item.reason || 'skipped') }));
  const error = typeof aiError === 'string' ? aiError.trim() : '';
  const warnings = [...new Set((Array.isArray(metadata.warnings) ? metadata.warnings : [])
    .filter((w) => typeof w === 'string' && w.trim())
    .map((w) => w.trim()))]
    .filter((w) => !(error && w.includes(error)));
  return { skipped, warnings };
}

/** How often an importing template is re-read while the worker runs (ms). */
export const IMPORT_POLL_MS = 4000;

/** Editor routes of one PowerPoint template (not the new-template route). */
const DECK_EDITOR_PATH = /^\/templates\/powerpoint-rich\/[^/]+\/edit$/;

/**
 * Leaving the new-template page for a template's own editor is never blocked: that move only
 * happens once a save or an import has created the template.
 * @param {string} from - Current pathname.
 * @param {string} to - Next pathname.
 * @returns {boolean} True to let the navigation through.
 */
function allowCreatedTemplateNavigation(from, to) {
  return from === templateEditorPath({ templateId: 'new', kind: 'powerpoint-rich' }) && DECK_EDITOR_PATH.test(to);
}

/** Template facts kept beside the slides (not edited directly). */
const EMPTY_META = Object.freeze({
  theme: null,
  sourceFileName: null,
  sourceDocument: null,
  generationMetadata: null,
  extractionWriterBrief: null,
  extractionAiApplied: null,
  extractionAiError: null,
  extractionAiSkippedReason: null,
  /** @type {'draft' | 'ready' | 'importing' | 'processing' | 'failed' | null} */
  status: null,
});

/**
 * Create or edit a JSON-based PowerPoint template with rich HTML per slide (the wave 1 editor).
 * @param {object} props - Props.
 * @param {function(object): void} [props.onDeckTemplate] - Called instead of loading a template
 *   whose content is a v2 deck template (`quill.deck-template`), which has its own editor.
 * @returns {React.ReactElement}
 */
function LegacyPowerPointRichTemplateEditor({ onDeckTemplate }) {
  const { templateId } = useParams();
  const onDeckTemplateRef = useRef(onDeckTemplate);
  onDeckTemplateRef.current = onDeckTemplate;
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { state: authState } = useAuth();
  const accountId = authState?.activeAccount?.id || null;
  // Read at call time (the actions object is rebuilt on every AppContext render).
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const [slides, setSlides] = useState(() => [
    {
      id: uuidv4(),
      type: 'title_slide',
      content: '<h1>{{presentation_title}}</h1><p>Presented by <strong>{{author}}</strong></p>',
    },
    {
      id: uuidv4(),
      type: 'content_slide',
      content:
        '<h2>Introduction</h2><ul><li>{{introduction_point_1}}</li><li>{{introduction_point_2}}</li><li>{{introduction_point_3}}</li></ul>',
    },
  ]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [templateMeta, setTemplateMeta] = useState(EMPTY_META);
  /** AI prompt per {{variable}} name (the catalog descriptions the worker sends to the model). */
  const [prompts, setPrompts] = useState({});

  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatedJson, setGeneratedJson] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [collapsedById, setCollapsedById] = useState({});
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [importFinished, setImportFinished] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [moveAnnouncement, setMoveAnnouncement] = useState('');
  /** Bumped whenever a load replaces the form, so the baseline is retaken even if nothing changed. */
  const [baselineEpoch, setBaselineEpoch] = useState(0);

  // Latest slides and prompts for handlers that run several times before a re-render (the
  // slide editor's onChange); every change goes through commitSlides / commitPrompts.
  const slidesRef = useRef(slides);
  const promptsRef = useRef(prompts);
  /** Variable names each slide lost in recent edits, so a rename keeps its prompt. */
  const pendingRenamesRef = useRef(new Map());
  const commitSlides = useCallback((next) => {
    slidesRef.current = next;
    setSlides(next);
  }, []);
  const commitPrompts = useCallback((next) => {
    promptsRef.current = next;
    setPrompts(next);
  }, []);

  const isEditing = Boolean(templateId && templateId !== 'new');
  /** Template id (or 'new') whose data is loaded; later template list updates do not reload it. */
  const loadedIdRef = useRef(null);
  /** Snapshot of the form as last loaded or saved (null until loaded); see formSnapshot. */
  const baselineRef = useRef(null);
  const needsBaselineRef = useRef(true);

  const importing = isImportingStatus(templateMeta.status);
  const listRowStatus = isEditing ? state.templates.find((t) => t.id === templateId)?.status : undefined;

  /**
   * Replace the form with a template's saved data and retake the unsaved-changes baseline.
   * @param {object} template - Template (list entry with content, or a full row).
   * @returns {void}
   */
  const applyTemplate = useCallback((template) => {
    setFormData({ name: template.name || '', description: template.description || '' });
    const status = template.status || null;
    const sourceDocument = template.sourceDocument ?? template.source_document ?? null;
    const generationMetadata = template.generationMetadata ?? template.generation_metadata ?? null;

    let parsed = null;
    if (typeof template.content === 'string' && template.content.trim()) {
      try {
        parsed = JSON.parse(template.content);
      } catch (error) {
        console.error('Error parsing template content:', error);
      }
    }
    if (parsed?.schema === DECK_TEMPLATE_SCHEMA && onDeckTemplateRef.current) {
      onDeckTemplateRef.current(template);
      return;
    }
    const hasSlides = Boolean(parsed) && Array.isArray(parsed.slides);
    if (hasSlides) {
      commitSlides(parsed.slides.map((slide) => ({
        ...slide,
        id: slide.id && typeof slide.id === 'string' ? slide.id : uuidv4(),
        type: slide.type || 'content_slide',
        content: typeof slide.content === 'string' ? slide.content : '<p></p>',
        ...(slide.aiNotes && typeof slide.aiNotes === 'object' ? { aiNotes: slide.aiNotes } : {}),
      })));
    } else if (isImportingStatus(status)) {
      // Nothing to show until the worker writes the slides.
      commitSlides([]);
    }
    commitPrompts(hasSlides ? promptsFromCatalog(parsed.variableCatalog) : {});
    pendingRenamesRef.current = new Map();
    setTemplateMeta({
      ...EMPTY_META,
      theme: parsed?.theme || null,
      sourceFileName: parsed?.sourceFileName || sourceDocument?.fileName || null,
      sourceDocument,
      generationMetadata,
      extractionWriterBrief: parsed?.extractionWriterBrief || null,
      extractionAiApplied: typeof parsed?.extractionAiApplied === 'boolean' ? parsed.extractionAiApplied : null,
      extractionAiError: parsed?.extractionAiError || null,
      extractionAiSkippedReason: parsed?.extractionAiSkippedReason || null,
      status,
    });
    needsBaselineRef.current = true;
    setBaselineEpoch((n) => n + 1);
  }, [commitPrompts, commitSlides]);

  /** The load in flight (`<id>:<attempt>`), so a later list update never starts a second one. */
  const fetchKeyRef = useRef(null);

  // Load once per template id, after AppContext has loaded the templates (a direct link to the
  // editor used to bounce to the list before the templates arrived). The list may not carry
  // `content`, and a template an import created moments ago may not be in it yet, so either
  // case reads the full row.
  useEffect(() => {
    const key = isEditing ? templateId : 'new';
    if (loadedIdRef.current === key) return;
    if (!isEditing) {
      loadedIdRef.current = key;
      needsBaselineRef.current = true;
      setBaselineEpoch((n) => n + 1);
      setLoadError('');
      setLoading(false);
      return;
    }
    if (!state.initialLoadComplete) {
      setLoading(true);
      return;
    }
    const listed = state.templates.find((t) => t.id === templateId);
    if (hasLoadedContent(listed)) {
      // A read in flight (that already put the row in the list) has nothing left to do.
      fetchKeyRef.current = null;
      applyTemplate(listed);
      loadedIdRef.current = key;
      setLoadError('');
      setLoading(false);
      return;
    }
    const fetchKey = `${key}:${loadAttempt}`;
    if (fetchKeyRef.current === fetchKey) return;
    fetchKeyRef.current = fetchKey;
    setLoading(true);
    loadFullTemplate({ actions: actionsRef.current, client: supabase, templateId, accountId })
      .then((row) => {
        if (fetchKeyRef.current !== fetchKey) return;
        if (!row) {
          navigate('/templates');
          return;
        }
        applyTemplate(row);
        loadedIdRef.current = key;
        setLoadError('');
      })
      .catch((error) => {
        if (fetchKeyRef.current !== fetchKey) return;
        console.error('Error loading template:', error);
        setLoadError('This template could not be loaded. Check your connection and try again.');
      })
      .finally(() => {
        if (fetchKeyRef.current === fetchKey) setLoading(false);
      });
  }, [templateId, isEditing, state.templates, state.initialLoadComplete, navigate, applyTemplate, accountId, loadAttempt]);

  // While the worker imports the deck, re-read the row until it is ready or failed (and right
  // away when the workspace list already shows the new status).
  useEffect(() => {
    if (!isEditing || loading || !importing) return undefined;
    let stopped = false;
    let inFlight = false;
    const check = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const row = await loadFullTemplate({ actions: actionsRef.current, client: supabase, templateId, accountId, force: true });
        if (stopped) return;
        if (!row) {
          // Deleted (for example by a quota rejection): the progress view explains it.
          stopped = true;
          return;
        }
        if (isImportingStatus(row.status)) return;
        stopped = true;
        applyTemplate(row);
        setImportFinished(true);
      } catch (error) {
        console.warn('Could not check the import yet:', error);
      } finally {
        inFlight = false;
      }
    };
    if (listRowStatus && !isImportingStatus(listRowStatus)) check();
    const timer = setInterval(check, IMPORT_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [isEditing, loading, importing, templateId, accountId, listRowStatus, applyTemplate]);

  const templateObject = useMemo(() => {
    const name = formData.name || 'My Rich Presentation Template';
    const catalog = buildPromptCatalog(slides, prompts);
    const template = {
      template_name: name,
      template_type: 'powerpoint',
      slides: slides.map(({ id, type, content, aiNotes }) => {
        const row = { id, type, content };
        if (aiNotes && typeof aiNotes === 'object') {
          row.aiNotes = aiNotes;
        }
        return row;
      }),
      variableCatalog: catalog,
    };
    if (templateMeta.theme) {
      template.theme = templateMeta.theme;
    }
    if (templateMeta.sourceFileName) {
      template.sourceFileName = templateMeta.sourceFileName;
    }
    if (templateMeta.extractionWriterBrief) {
      template.extractionWriterBrief = templateMeta.extractionWriterBrief;
    }
    if (typeof templateMeta.extractionAiApplied === 'boolean') {
      template.extractionAiApplied = templateMeta.extractionAiApplied;
    }
    if (templateMeta.extractionAiError) {
      template.extractionAiError = templateMeta.extractionAiError;
    }
    if (templateMeta.extractionAiSkippedReason) {
      template.extractionAiSkippedReason = templateMeta.extractionAiSkippedReason;
    }
    return template;
  }, [slides, prompts, formData.name, templateMeta]);

  const suggestions = useMemo(() => suggestedPrompts(slides), [slides]);
  const slidesByName = useMemo(() => slidesByVariable(slides), [slides]);

  useEffect(() => {
    setGeneratedJson(JSON.stringify(templateObject, null, 2));
  }, [templateObject]);

  /** Everything a save writes, as one comparable string (dirty = differs from the baseline). */
  const formSnapshot = useMemo(
    () => JSON.stringify([formData.name, formData.description, templateObject]),
    [formData.name, formData.description, templateObject]
  );
  const formSnapshotRef = useRef(formSnapshot);
  formSnapshotRef.current = formSnapshot;
  /** The slides and prompts alone (the name and description are kept by an import). */
  const slidesSnapshot = useMemo(() => JSON.stringify([templateObject.slides, prompts]), [templateObject.slides, prompts]);
  const slidesSnapshotRef = useRef(slidesSnapshot);
  slidesSnapshotRef.current = slidesSnapshot;
  const slidesBaselineRef = useRef(null);

  // Baseline right after a load, so loading itself never counts as an edit.
  useEffect(() => {
    if (loading || !needsBaselineRef.current) return;
    needsBaselineRef.current = false;
    baselineRef.current = formSnapshot;
    slidesBaselineRef.current = slidesSnapshot;
  }, [loading, formSnapshot, slidesSnapshot, baselineEpoch]);

  /**
   * Whether the slides or prompts were edited since the last load or save (an import on the new
   * template page would replace them).
   * @returns {boolean}
   */
  const slidesEdited = useCallback(
    () => slidesBaselineRef.current !== null && slidesSnapshotRef.current !== slidesBaselineRef.current,
    []
  );

  /**
   * Whether there are edits since the last load or save.
   * @returns {boolean} True with unsaved edits.
   */
  const isDirty = useCallback(
    () => baselineRef.current !== null && formSnapshotRef.current !== baselineRef.current,
    []
  );

  const addSlide = () => {
    const newSlide = {
      id: uuidv4(),
      type: 'content_slide',
      content: '<p>Enter slide content here...</p>',
    };
    commitSlides([...slidesRef.current, newSlide]);
  };

  const removeSlide = (id) => {
    pendingRenamesRef.current.delete(id);
    commitSlides(slidesRef.current.filter((slide) => slide.id !== id));
  };

  /**
   * Change one slide field. Content changes also move a renamed variable's prompt to its new name.
   * @param {string} id - Slide id.
   * @param {string} field - Field name.
   * @param {*} value - New value.
   * @returns {void}
   */
  const updateSlide = useCallback((id, field, value) => {
    const current = slidesRef.current;
    const before = current.find((slide) => slide.id === id);
    if (!before) return;
    if (field === 'content') {
      const namesElsewhere = new Set(
        current.filter((slide) => slide.id !== id).flatMap((slide) => variableNamesInHtml(slide.content))
      );
      const carried = carryPromptsAcrossRename({
        prevHtml: before.content,
        nextHtml: value,
        namesElsewhere,
        prompts: promptsRef.current,
        pending: pendingRenamesRef.current.get(id) || [],
      });
      pendingRenamesRef.current.set(id, carried.pending);
      if (carried.prompts !== promptsRef.current) commitPrompts(carried.prompts);
    }
    commitSlides(current.map((slide) => (slide.id === id ? { ...slide, [field]: value } : slide)));
  }, [commitPrompts, commitSlides]);

  /**
   * Edit the AI prompt of one variable (shared by every slide that uses it).
   * @param {string} name - Variable name.
   * @param {string} value - Prompt text.
   * @returns {void}
   */
  const updatePrompt = useCallback((name, value) => {
    commitPrompts({ ...promptsRef.current, [name]: String(value).slice(0, MAX_PROMPT_LENGTH) });
  }, [commitPrompts]);

  const moveSlide = (fromIndex, toIndex) => {
    const updatedSlides = [...slidesRef.current];
    const [movedSlide] = updatedSlides.splice(fromIndex, 1);
    updatedSlides.splice(toIndex, 0, movedSlide);
    commitSlides(updatedSlides);
  };

  /** Keyboard or button move: same as a drag, plus a screen reader announcement. */
  const moveSlideFromKeyboard = (fromIndex, toIndex) => {
    const total = slidesRef.current.length;
    if (toIndex < 0 || toIndex >= total || fromIndex === toIndex) return;
    moveSlide(fromIndex, toIndex);
    setMoveAnnouncement(`Slide ${fromIndex + 1} moved to position ${toIndex + 1} of ${total}.`);
  };

  const toggleSlideCollapsed = (id) => {
    setCollapsedById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const collapseAll = () => {
    setCollapsedById(Object.fromEntries(slides.map((s) => [s.id, true])));
  };

  const expandAll = () => {
    setCollapsedById(Object.fromEntries(slides.map((s) => [s.id, false])));
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(generatedJson)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = generatedJson;
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
          console.error('Failed to copy JSON: ', err);
        }
        document.body.removeChild(textarea);
      });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (slides.length === 0) {
      newErrors.slides = 'At least one slide is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Validate and write the template (handleSave serializes calls to this).
   * @param {{stay?: boolean, leaving?: boolean}} [opts] - `stay`: stay on the page (logout flush,
   *   leave prompt) instead of returning to the list; `leaving`: a navigation is waiting, so a new
   *   template does not move to its own URL.
   * @returns {Promise<boolean>} True when everything edited so far is saved.
   */
  const saveTemplate = async ({ stay = false, leaving: isLeaving = false } = {}) => {
    // The worker owns the row until the import finishes; a failed load has nothing to save.
    if (importing || loadError) return false;
    if (!validateForm()) return false;

    setSaving(true);
    setErrors({});
    const savedSnapshot = formSnapshot;

    try {
      // A draft import, or a failed one the user built by hand, becomes ready on save.
      const promotesToReady = templateMeta.status === 'draft' || templateMeta.status === 'failed' || !isEditing;
      const templateData = {
        ...formData,
        // Built from the current state (generatedJson trails it by one effect).
        content: JSON.stringify(templateObject, null, 2),
        template_type: 'powerpoint',
        // Saving a draft for the first time promotes it to ready-for-generation.
        ...(promotesToReady ? { status: 'ready' } : {}),
      };

      // AppContext reports failures as { success: false } instead of throwing.
      const result = isEditing
        ? await actions.updateTemplate(templateId, templateData)
        : await actions.createTemplate(templateData);
      if (!result || result.success === false) throw new Error(result?.error || 'Save failed');

      baselineRef.current = savedSnapshot;
      if (!stay) {
        navigate('/templates');
        return true;
      }
      if (promotesToReady) setTemplateMeta((prev) => ({ ...prev, status: 'ready' }));
      const newId = !isEditing ? result?.data?.id : null;
      if (newId && !isLeaving) {
        // Keep editing the created template at its own URL without reloading the form.
        loadedIdRef.current = newId;
        navigate(templateEditorPath({ templateId: newId, kind: 'powerpoint-rich' }), { replace: true });
      }
      return formSnapshotRef.current === savedSnapshot;
    } catch (error) {
      console.error('Error saving template:', error);
      setErrors({ general: 'Failed to save template. Please try again.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveTemplateRef = useRef(saveTemplate);
  saveTemplateRef.current = saveTemplate;
  /** The running save, so overlapping triggers (button, logout flush) never write twice. */
  const savePromiseRef = useRef(null);

  /**
   * Save the template, one save at a time: a trigger that arrives while a save runs waits for
   * it and only saves again if there are still unsaved edits.
   * @param {{stay?: boolean}} [opts] - Stay on the page instead of returning to the list.
   * @returns {Promise<boolean>} True when everything edited so far is saved.
   */
  const handleSave = useCallback(async (opts) => {
    while (savePromiseRef.current) {
      await savePromiseRef.current.catch(() => false);
      if (!isDirty()) return true;
    }
    const attempt = saveTemplateRef.current(opts);
    savePromiseRef.current = attempt;
    try {
      return await attempt;
    } finally {
      if (savePromiseRef.current === attempt) savePromiseRef.current = null;
    }
  }, [isDirty]);

  // Actions that unmount the page without a router navigation (logging out) save pending
  // edits through this first.
  useEffect(
    () => registerUnsavedWorkFlusher(async () => (isDirty() ? handleSave({ stay: true }) : true)),
    [handleSave, isDirty]
  );

  // Leaving with unsaved edits (back, links, Inky, refresh, tab close) asks first.
  const blocker = useUnsavedChangesGuard(isDirty, { allowNavigation: allowCreatedTemplateNavigation });
  const leaveBlocked = blocker.state === 'blocked';

  /** Leave prompt: save, then continue the navigation (or stay when the save failed). */
  const handleSaveAndLeave = async () => {
    setLeaving(true);
    let saved = false;
    try {
      saved = await handleSave({ stay: true, leaving: true });
    } finally {
      setLeaving(false);
    }
    if (saved) blocker.proceed?.();
    else blocker.reset?.();
  };

  const handleCancel = () => {
    navigate('/templates');
  };

  /** The live import view reports the import finished: load the finished deck now. */
  const handleImportReady = useCallback(async () => {
    if (!isEditing) return;
    try {
      const row = await loadFullTemplate({ actions: actionsRef.current, client: supabase, templateId, accountId, force: true });
      if (!row || isImportingStatus(row.status)) return;
      applyTemplate(row);
      setImportFinished(true);
    } catch (error) {
      console.warn('Could not load the imported template yet:', error);
    }
  }, [isEditing, templateId, accountId, applyTemplate]);

  /**
   * Keep the editor's status in step with the live import view: a retry started there makes a
   * failed template 'importing' again (Save is locked while the worker runs), and an import the
   * view finds failed or stuck unlocks the slides for building by hand.
   * @param {string} stage - deriveImportStage stage.
   */
  const handleImportStage = useCallback((stage) => {
    if (stage === 'ready') return;
    setTemplateMeta((meta) => {
      if (stage === 'failed') return isImportingStatus(meta.status) ? { ...meta, status: 'failed' } : meta;
      return meta.status === 'failed' ? { ...meta, status: 'importing' } : meta;
    });
  }, []);

  const countVariables = (slideList) => {
    const allText = slideList.map((slide) => `${slide.content || ''}`).join(' ');
    const matches = allText.match(/\{\{[^}]+\}\}/g);
    return matches ? matches.length : 0;
  };

  if (loading) {
    return (
      <Box role="progressbar" aria-label="Loading template" aria-busy="true" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Skeleton variant="rounded" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={240} height={28} />
            <Skeleton variant="text" width={140} height={18} />
          </Box>
          <Skeleton variant="rounded" width={120} height={36} sx={{ borderRadius: 999 }} />
        </Box>
        {[0, 1].map((key) => (
          <Skeleton key={key} variant="rounded" height={220} sx={{ mb: 2, maxWidth: 900 }} />
        ))}
      </Box>
    );
  }

  const variableCount = countVariables(slides);
  const importNotes = importNotesFrom(templateMeta.generationMetadata, templateMeta.extractionAiError);
  const showImportProgress = isEditing && (importing || templateMeta.status === 'failed');
  const canSave = !importing && !loadError;
  const showSlides = !importing && !loadError;

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <Box
          component="header"
          sx={{
            position: 'sticky',
            top: 'var(--q-page-sticky-top, 0px)',
            zIndex: 20,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 1, sm: 2 },
            py: 1,
            bgcolor: 'var(--q-surface)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Back to templates">
            <IconButton onClick={handleCancel} aria-label="Back to templates">
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <IconTile size={36}><SlideshowOutlined /></IconTile>
          </Box>
          <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <InputBase
              value={formData.name}
              placeholder={isEditing ? 'Template name' : 'Name your PowerPoint template'}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              readOnly={!canSave}
              inputProps={{ 'aria-label': 'Template name', maxLength: 200, 'aria-invalid': Boolean(errors.name) }}
              sx={{
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                maxWidth: 480,
                '& input': { px: 0.75, py: '1px', ml: -0.75, borderRadius: '6px', border: '1px solid transparent' },
                '& input:hover': { borderColor: 'divider' },
                '& input:focus': { borderColor: 'var(--q-accent)' },
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: 12.5, color: 'text.secondary' }}>
              <span>Templates</span>
              <span aria-hidden="true">·</span>
              <span>PowerPoint</span>
              <span aria-hidden="true">·</span>
              <span>{slides.length} slide{slides.length === 1 ? '' : 's'}, {variableCount} variable{variableCount === 1 ? '' : 's'}</span>
              {templateMeta.status === 'draft' && <Chip label="Draft" size="small" color="warning" />}
              {importing && <Chip label="Importing" size="small" color="info" />}
              {templateMeta.status === 'failed' && <Chip label="Import failed" size="small" color="error" />}
            </Box>
          </Box>
          <Tooltip title="How PowerPoint templates work">
            <IconButton aria-label="PowerPoint template help" onClick={() => setHelpDialogOpen(true)}>
              <Help />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            disabled={!showSlides}
            onClick={() => setPreviewMode(!previewMode)}
            startIcon={previewMode ? <EditOutlined /> : <Preview />}
            aria-pressed={previewMode}
            sx={{ borderRadius: 999 }}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button
            onClick={() => handleSave()}
            variant="contained"
            disabled={saving || !canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ borderRadius: 999 }}
          >
            {saving ? 'Saving…' : isEditing ? 'Save template' : 'Create template'}
          </Button>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(errors.name || errors.general) && (
            <Alert severity="error">{errors.name || errors.general}</Alert>
          )}
          {loadError && (
            <Alert
              severity="error"
              action={(
                <Button color="inherit" size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
                  Try again
                </Button>
              )}
            >
              {loadError}
            </Alert>
          )}
          {showImportProgress && (
            <PresentationImportProgress
              templateId={templateId}
              onReady={handleImportReady}
              onStageChange={handleImportStage}
              onBack={handleCancel}
            />
          )}
          {importFinished && !importing && templateMeta.status !== 'failed' && (
            <Alert severity="success" onClose={() => setImportFinished(false)}>
              Import finished. Review the slides and the AI prompt under each slot, then save.
            </Alert>
          )}
          {templateMeta.status === 'draft' && (
            <Alert severity="info">
              This template is a <strong>draft</strong> imported from PowerPoint. Review the slides and the AI prompt
              under each slot, then save to make it available for document generation.
            </Alert>
          )}
          {templateMeta.extractionAiError && (
            <Alert severity="warning">
              AI restructuring reported an issue: {templateMeta.extractionAiError}. Heuristic slides were kept where
              the AI pass did not succeed.
            </Alert>
          )}
          {templateMeta.extractionAiSkippedReason && !templateMeta.extractionAiError && (
            <Alert severity="info">AI refinement was skipped: {templateMeta.extractionAiSkippedReason}</Alert>
          )}
          {!importing && templateMeta.status !== 'failed' && importNotes.skipped.length + importNotes.warnings.length > 0 && (
            <Alert severity="warning" data-testid="import-notes">
              {importNotes.skipped.length > 0 && (
                <>
                  {importNotes.skipped.length === 1 ? 'One slide was not imported:' : `${importNotes.skipped.length} slides were not imported:`}
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {importNotes.skipped.map((item) => (
                      <li key={`skipped-${item.index}`}>Slide {item.index + 1}: {item.reason}</li>
                    ))}
                  </Box>
                </>
              )}
              {importNotes.warnings.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: importNotes.skipped.length ? 1 : 0, pl: 2.5 }}>
                  {importNotes.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', lg: 'nowrap' }, alignItems: 'flex-start', gap: 3 }}>
            <Box sx={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  InputProps={{ readOnly: !canSave }}
                  fullWidth
                  multiline
                  maxRows={3}
                />
                {!isEditing && (
                  <PowerPointUploadSection name={formData.name} description={formData.description} confirmReplace={slidesEdited} />
                )}
                {(templateMeta.sourceFileName || templateMeta.sourceDocument?.path) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {templateMeta.sourceFileName && (
                      <Chip label={`Source: ${templateMeta.sourceFileName}`} size="small" variant="outlined" />
                    )}
                    {templateMeta.sourceDocument?.path && (
                      <Chip label="Original file kept" size="small" variant="outlined" color="success" />
                    )}
                  </Box>
                )}
              </Box>

              {showSlides && (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ flex: 1, fontSize: 15 }}>
                      Slides
                      <Typography component="span" sx={{ ml: 1, fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>{slides.length}</Typography>
                    </Typography>
                    {!previewMode && slides.length > 1 && (
                      <>
                        <Button size="small" onClick={collapseAll}>Collapse all</Button>
                        <Button size="small" onClick={expandAll}>Expand all</Button>
                      </>
                    )}
                    {!previewMode && (
                      <Button onClick={addSlide} startIcon={<Add />} variant="contained" size="small">
                        Add slide
                      </Button>
                    )}
                  </Box>

                  {slides.length === 0 ? (
                    <Box sx={{ bgcolor: 'background.paper', border: '1px dashed var(--q-border-hover)', borderRadius: 'var(--q-radius-card)' }}>
                      <EmptyState
                        compact
                        icon={<SlideshowOutlined />}
                        title="No slides yet"
                        description={isEditing ? 'Add a slide to start building this deck.' : 'Import a .pptx above or add a slide to start.'}
                        actions={<Button onClick={addSlide} startIcon={<Add />} variant="contained">Add first slide</Button>}
                      />
                    </Box>
                  ) : (
                    <Box>
                      {slides.map((slide, index) => (
                        <DraggableSlide
                          key={slide.id}
                          index={index}
                          slide={slide}
                          updateSlide={updateSlide}
                          removeSlide={removeSlide}
                          moveSlide={moveSlide}
                          collapsed={!!collapsedById[slide.id]}
                          onToggleCollapsed={toggleSlideCollapsed}
                          readOnly={previewMode}
                          prompts={prompts}
                          suggestions={suggestions}
                          slidesByName={slidesByName}
                          onPromptChange={updatePrompt}
                          slideCount={slides.length}
                          onKeyboardMove={moveSlideFromKeyboard}
                        />
                      ))}
                    </Box>
                  )}

                  <Box role="status" aria-live="polite" sx={VISUALLY_HIDDEN}>{moveAnnouncement}</Box>
                  {errors.slides && <Alert severity="error">{errors.slides}</Alert>}
                </>
              )}
            </Box>

            <Box component="aside" aria-label="Template data" sx={{ flex: '1 0 300px', maxWidth: { xs: 'none', lg: 380 }, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
              <PowerPointJsonPanel generatedJson={generatedJson} isCopied={isCopied} onCopy={copyToClipboard} />
            </Box>
          </Box>
        </Box>

        <PowerPointHelpDialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
        <UnsavedChangesDialog
          open={leaveBlocked}
          saving={leaving}
          canSave={canSave}
          onSave={handleSaveAndLeave}
          onDiscard={() => blocker.proceed?.()}
          onStay={() => blocker.reset?.()}
        />
      </Box>
    </DndProvider>
  );
}

/**
 * PowerPoint template route: a v2 deck template (`content.schema === 'quill.deck-template'`)
 * opens in DeckTemplateEditor, every other template in the wave 1 editor. The wave 1 editor
 * loads the row (and follows a running import), then hands a v2 row over.
 * @returns {React.ReactElement}
 */
function PowerPointRichTemplateEditor() {
  const { templateId } = useParams();
  const [deck, setDeck] = useState(null);
  const handleDeckTemplate = useCallback((row) => setDeck({ templateId, row }), [templateId]);
  if (deck && deck.templateId === templateId) {
    return <DeckTemplateEditor key={templateId} templateId={templateId} initialRow={deck.row} />;
  }
  return <LegacyPowerPointRichTemplateEditor onDeckTemplate={handleDeckTemplate} />;
}

export default PowerPointRichTemplateEditor;
